import React, { createContext, useMemo, useEffect, useState, useContext, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'

import {
    useIntl,
    useToggle,
    useConfirm,
    useMutation,
    useFileFavorites,
    useSelectable,
    useFileDownload,
} from 'hooks'
import { generateQuery, getSummaryFromFiles } from 'utils/filter'
import { PATIENT_FILE_FILTERS } from 'app/constants'
import { useGetCollectionFilesQuery, useDeleteFilesFromCollectionMutation } from 'services'
import { set } from 'features/file-view/fileSlice'
import { CollectionContext } from 'features/surgical-cloud/PatientsCollections'
import AddToCollection from 'features/add-files-collection'
import Edit from 'features/collection/Edit'
import Tags from 'features/tags'
import {
    collectionSelector,
    updateCollection,
    deleteCollection,
} from 'features/collections-list/collectionSlice'
import Share from 'features/share/Share'
import CollectionDetailsHeader from 'components/Collection/CollectionDetailsHeader'
import CollectionDetailsFallback from 'components/Fallback/Details'
import FilesFilter from 'components/FilesUIFilter'
import SelectedFileActions from 'components/SelectedFileActions'
import ThumbnailsContainer from './ThumbnailsContainer'
import { Loader } from 'components/Loaders'
import { Checkbox, ScrollableDirective } from 'components/Zui'
import './index.css'

export const FileContext = createContext(null)

const CollectionDetails = () => {
    const { t } = useIntl()
    const history = useHistory()
    const reduxDispatch = useDispatch()
    const { getConfirmation } = useConfirm()
    const [shareableCollection, setShareableCollection] = useState()
    const { collectionId } = useContext(CollectionContext)
    const [filter, setFilter] = useState(null)
    const {
        isOpen: isCollectionOpen,
        onOpen: onCollectionOpen,
        onClose: onCollectionClose,
    } = useToggle()

    const {
        isOpen: isCollectionEditOpen,
        onOpen: onCollectionEditOpen,
        onClose: onCollectionEditClose,
    } = useToggle()

    const { isOpen: isTagOpen, onOpen: onTagOpen, onClose: onTagClose } = useToggle()
    const { isOpen: isShareOpen, onOpen: onShareOpen, onClose: onShareClose } = useToggle()
    const collection = useSelector(state => collectionSelector.selectById(state, collectionId))
    const { filterParams, uiFilter } = useSelector(state => state.collections)
    const { download } = useFileDownload('files')

    /*
     *  Data will be cashed for 20 minutes (1200 ms).
     *  To update cache time, change value of `keepUnusedDataFor` in service endpoint
     */
    const { data, isLoading, isFetching, isError } = useGetCollectionFilesQuery(
        {
            id: collectionId,
            query: generateQuery(filterParams.filter(p => ['sort', 'filter'].includes(p.type))),
        },
        {
            skip: !collectionId,
        }
    )

    const { actions, dispatch, selectable, handleSelectFile, handleSelectAll, isSelectedAll } =
        useSelectable(data?.files, filter)

    const { isAllFavorite, favoriteFileIds, toggleFavorite, toggleAllFavorite } = useFileFavorites(
        data?.files,
        selectable.selectedFiles
    )

    const [deleteFilesFromCollection] = useMutation(useDeleteFilesFromCollectionMutation, {
        trigger: [
            {
                dispatch: reduxDispatch,
                actions: [() => handleSelectFile(false, selectable.selectedFiles)],
            },
        ],
    })

    useEffect(() => {
        setFilter(uiFilter)
        dispatch(actions.unSelectAll())
    }, [collectionId])

    const getSingleFileType = useCallback(() => {
        if (selectable.selectedFiles.length > 1) return
        const fileId = selectable.selectedFiles[0]
        return data?.files.find(f => f.fileId === fileId).fileType.toLowerCase()
    }, [selectable, data])

    const getFilteredFileIds = useCallback(() => {
        let fileIds = []
        if (filter !== 'all') {
            fileIds = data?.files.filter(t => t.fileType === filter).map(t => t.fileId) || []
        } else {
            fileIds = data?.files.map(f => f.fileId) || []
        }
        return fileIds
    }, [filter, data])

    const handleFileView = useCallback(
        id => {
            const fileIds = getFilteredFileIds()
            const viewFiles = data?.files.filter(t => fileIds.includes(t.fileId)) || []
            reduxDispatch(set(viewFiles))
            history.push({
                pathname: '/files/' + id,
                key: id,
                state: {
                    actionType: 'file-view',
                    tab: 'collections',
                    id: collectionId,
                    filter,
                },
            })
        },
        [filter, data]
    )

    const handleDeleteFilesFromCollection = async () => {
        const confirmed = await getConfirmation({
            title: t('files.confirm.delete.title'),
            message: t('files.confirm.delete.message'),
            buttons: [t('cancel'), t('delete')],
        })

        if (confirmed) {
            /*
             * Get the updated summary by subtracting the count of deleted filetypes
             */
            const summary = getSummaryFromFiles(selectable.selectedFiles, data.files, data.summary)
            const payload = {
                id: collectionId,
                body: {
                    fileIds: selectable.selectedFiles.join(),
                },
                query: generateQuery(filterParams.filter(p => ['sort', 'filter'].includes(p.type))),
                summary,
            }

            deleteFilesFromCollection(payload)
        }
    }

    const handleUpdateCollection = values => {
        reduxDispatch(updateCollection({ id: values.id, data: { collectionName: values.name } }))
        onCollectionEditClose()
    }

    const handleDeleteCollection = async () => {
        const confirmed = await getConfirmation({
            title: t('collection.confirm.delete.title'),
            message: t('collection.confirm.delete.message'),
            buttons: [t('cancel'), t('delete')],
        })
        if (confirmed) {
            reduxDispatch(deleteCollection(collectionId))
        }
    }

    const handleShareCollection = () => {
        setShareableCollection({
            type: 'collection',
            subType: filter,
            id: collectionId,
            name: collection.name,
        })
        onShareOpen()
    }

    const handleShareFiles = () => {
        setShareableCollection({
            type: 'files',
            fileIds: selectable.selectedFiles,
            fileType: getSingleFileType(),
            name:
                selectable.selectedFiles.length > 1
                    ? selectable.selectedFiles.length + ' files'
                    : null,
            collection: {
                name: collection.name,
            },
        })
        onShareOpen()
    }

    const handleDownloadCollection = () => {
        if (!data || !data.summary.all) return

        download({
            type: 'collection',
            subType: filter,
            id: collectionId,
        })
    }

    const handleDownloadFiles = () => {
        if (!selectable.selectedFiles.length) return

        download({
            fileIds: selectable.selectedFiles,
        })
        handleSelectFile(false, selectable.selectedFiles)
    }

    const handleAllFavorite = () => {
        toggleAllFavorite(true)
    }

    const filterOptions = PATIENT_FILE_FILTERS.map(op => {
        return { ...op, count: data?.summary[op.value] || 0 }
    })

    const canRenderThumbnails = !isLoading && !isFetching && data?.summary.all

    const RenderThumbnailsContainer = useMemo(
        () => (
            <div className="collection-item-grid-view-wrapper">
                <ScrollableDirective
                    style={{
                        maxHeight: '100%',
                        width: '100%',
                        flexGrow: '1',
                        overflow: 'auto',
                    }}
                >
                    <div className="collection-item-grid-view-container">
                        <div className="header-wrap">
                            <div className="accordion-data-wrapper">
                                <div className="accordion-data-container">
                                    <ThumbnailsContainer
                                        files={data?.files}
                                        filter={filter}
                                        isLoading={false}
                                        handleFavorite={toggleFavorite}
                                        handleClick={handleFileView}
                                        handleSelectFile={handleSelectFile}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </ScrollableDirective>
            </div>
        ),
        [data, filter, favoriteFileIds]
    )

    const contextMenu = [
        {
            title: t('share'),
            icon: 'share',
            onClick: handleShareCollection,
        },
        {
            title: t('download'),
            icon: 'downloadIcnDark',
            onClick: handleDownloadCollection,
        },
        {
            title: t('delete'),
            icon: 'delete',
            onClick: handleDeleteCollection,
        },
        {
            title: t('edit'),
            icon: 'edit',
            onClick: onCollectionEditOpen,
        },
    ]

    return (
        <FileContext.Provider value={{ selectable, actions, dispatch }}>
            <div className="content-container">
                <div className="collection-details-wrapper">
                    {collectionId ? (
                        <>
                            <div className="patient-contents-header">
                                <CollectionDetailsHeader contextMenu={contextMenu} />
                                <div className="filter-select-options-wrapper">
                                    <div className="files-filter-wrapper">
                                        <FilesFilter
                                            isDisabled={!Boolean(data?.summary.all)}
                                            value={filter}
                                            onChange={setFilter}
                                            options={filterOptions}
                                        />
                                        <Checkbox
                                            style={{ display: ' flex', fontSize: ' 14px' }}
                                            theme="zbds-dark"
                                            name="selectAll"
                                            disabled={!Boolean(data?.summary.all)}
                                            value={isSelectedAll}
                                            onChange={e => handleSelectAll(e.target.value)}
                                        >
                                            <span style={{ color: '#D1D9E5' }}>
                                                {t('select.all')}
                                            </span>
                                        </Checkbox>
                                    </div>
                                    {Boolean(
                                        selectable.selectedFiles.length && data?.summary[filter]
                                    ) && (
                                        <SelectedFileActions
                                            actions={[
                                                {
                                                    icon: 'share',
                                                    onClick: handleShareFiles,
                                                },
                                                {
                                                    icon: isAllFavorite
                                                        ? 'favoriteFilled'
                                                        : 'favorite',
                                                    onClick: handleAllFavorite,
                                                },
                                                {
                                                    icon: 'moveFolder',
                                                    onClick: onCollectionOpen,
                                                },
                                                {
                                                    icon: 'tag',
                                                    onClick: onTagOpen,
                                                },
                                                {
                                                    icon: 'download',
                                                    onClick: handleDownloadFiles,
                                                },
                                                {
                                                    icon: 'delete',
                                                    onClick: handleDeleteFilesFromCollection,
                                                },
                                            ]}
                                        />
                                    )}
                                </div>
                            </div>
                            {isLoading && <Loader />}
                            {!isLoading && !isFetching && (!data?.summary.all || isError) && (
                                <CollectionDetailsFallback
                                    title={t('fallback.no.media')}
                                    icon="image"
                                />
                            )}
                            {canRenderThumbnails && RenderThumbnailsContainer}
                        </>
                    ) : (
                        <CollectionDetailsFallback
                            title={t('fallback.no.collection.selected')}
                            icon="folder"
                        />
                    )}
                </div>
                {isCollectionOpen && (
                    <AddToCollection
                        onClose={onCollectionClose}
                        handleSelectFile={handleSelectFile}
                        data={{ type: 'files', fileIds: selectable.selectedFiles }}
                    />
                )}
                {isCollectionEditOpen && (
                    <Edit
                        onClose={onCollectionEditClose}
                        data={collection}
                        onSubmit={handleUpdateCollection}
                    />
                )}
                {isTagOpen && (
                    <Tags
                        onClose={onTagClose}
                        data={{ type: 'files', fileIds: selectable.selectedFiles }}
                    />
                )}
                {isShareOpen && <Share onClose={onShareClose} data={shareableCollection} />}
            </div>
        </FileContext.Provider>
    )
}

export default React.memo(CollectionDetails)
